import { isJournalName, type JournalData, type JournalFile } from './journal.ts';
import type { LocalStorageFiles } from './storage.ts';
import { api, takeUrl } from './api.ts';
import { saveFolder } from './save-folder.ts';

/**
 * The recordings kept on this computer, listed from their take journals, and ways to get them out of the
 * browser's private storage without the server: copy a whole take into a folder (Chrome, Edge) or download
 * each file (every browser).
 */
export interface LocalTake {
  journalName: string;
  journal: JournalData;
  /** `recording`: a recorder (in this or another tab) is still writing it, so it can't be copied yet. */
  files: { entry: JournalFile; size: number; recording: boolean }[];
}

export async function listLocalTakes(storage: LocalStorageFiles): Promise<LocalTake[]> {
  const listing = await storage.list();
  const sizes = new Map(listing.map((f) => [f.name, f.size]));
  // Recorders hold a lock on their file for as long as they write it.
  const held = new Set(((await navigator.locks?.query().catch(() => null))?.held ?? []).map((l) => l.name));
  const takes: LocalTake[] = [];
  for (const { name } of listing.filter((f) => isJournalName(f.name))) {
    try {
      const journal = JSON.parse(await storage.readText(name)) as JournalData;
      if (journal?.format !== 'takehouse-journal@1') continue;
      const files = journal.files
        .filter((f) => (sizes.get(f.localName) ?? 0) > 0)
        .map((entry) => ({ entry, size: sizes.get(entry.localName)!, recording: held.has(`takehouse:${entry.localName}`) }));
      if (files.length) takes.push({ journalName: name, journal, files });
    } catch {}
  }
  return takes.sort((a, b) => b.journal.createdAt - a.journal.createdAt);
}

const safe = (s: string) => s.replace(/[^\w.-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'file';

/** A readable name that says which take, person and source a file is. */
export const exportName = (t: LocalTake, f: JournalFile) =>
  `T${t.journal.takeNumber}_${safe(t.journal.participantName)}_${safe(f.label)}_${f.localName.split('-').pop()}`;

export const canSaveToFolder = () => typeof (window as any).showDirectoryPicker === 'function';

/**
 * Copies a take's files and its journal into a folder: the one given (the automatic save folder), or one the
 * person picks. Reads in 8 MB pieces through the storage worker, so even long recordings never sit in memory
 * whole. For hosts and producers the folder is laid out like the studio's own: files under their studio names in
 * media/, with the take's timeline.fcpxml and manifest.json beside them, so it opens as it is in an editor.
 * Copying again replaces what's there. Returns the folder's name.
 */
export async function saveTakeToFolder(
  storage: LocalStorageFiles,
  take: LocalTake,
  opts: { root?: FileSystemDirectoryHandle; onProgress?: (done: number, total: number) => void } = {},
) {
  const root: FileSystemDirectoryHandle = opts.root ?? (await (window as any).showDirectoryPicker({ id: 'wordstow', mode: 'readwrite', startIn: 'documents' }));
  const folder = await root.getDirectoryHandle(`${safe(take.journal.studioName || 'Wordstow')}-take-${take.journal.takeNumber}-${safe(take.journal.participantName)}`, { create: true });
  const studio = await studioLayout(take).catch(() => null);
  const media = studio ? await folder.getDirectoryHandle('media', { create: true }) : folder;
  const files = take.files.filter((f) => !f.recording);
  const total = files.reduce((a, f) => a + f.size, 0);
  let done = 0;
  for (const { entry, size } of files) {
    const name = (entry.trackId && studio?.names.get(entry.trackId)) || exportName(take, entry);
    const out = await (await media.getFileHandle(name, { create: true })).createWritable();
    try {
      for (let at = 0; at < size; at += 8 << 20) {
        const chunk = await storage.read(entry.localName, at, Math.min(size, at + (8 << 20)));
        await out.write(chunk as Uint8Array<ArrayBuffer>);
        done += chunk.length;
        opts.onProgress?.(done, total);
      }
    } finally {
      await out.close();
    }
  }
  const write = async (name: string, text: string) => {
    const out = await (await folder.getFileHandle(name, { create: true })).createWritable();
    await out.write(text);
    await out.close();
  };
  if (studio) {
    await write('timeline.fcpxml', studio.timeline);
    await write('manifest.json', studio.manifest);
  }
  await write('journal.json', JSON.stringify(take.journal, null, 2));
  saveFolder().markSaved(take.journalName, `${root.name}/${folder.name}`);
  return folder.name;
}

/** The studio's file names, timeline and manifest for this take (hosts and producers; null if unavailable). */
async function studioLayout(take: LocalTake) {
  const j = take.journal;
  if (j.role === 'guest') return null;
  const info = (await api.takes(j.studioId, j.key)).find((t) => t.id === j.takeId);
  if (!info) return null;
  const text = async (file: string) => {
    const res = await fetch(takeUrl(j.takeId, j.key, file));
    if (!res.ok) throw new Error(`${file}: ${res.status}`);
    return res.text();
  };
  const [timeline, manifest] = await Promise.all([text('timeline.fcpxml'), text('manifest.json')]);
  return { names: new Map(info.tracks.map((t) => [t.id, t.fileName])), timeline, manifest };
}

/**
 * Downloads one file. The storage worker lets go of the file first, so the browser can hand over the file
 * on disk itself rather than a copy in memory.
 */
export async function downloadLocalFile(storage: LocalStorageFiles, localName: string, downloadName: string) {
  await storage.close(localName);
  const dir = await (await navigator.storage.getDirectory()).getDirectoryHandle('takehouse');
  const file = await (await dir.getFileHandle(localName)).getFile();
  saveBlob(file, downloadName);
}

export function downloadJournal(take: LocalTake) {
  saveBlob(new Blob([JSON.stringify(take.journal, null, 2)], { type: 'application/json' }), `T${take.journal.takeNumber}_${safe(take.journal.participantName)}_journal.json`);
}

function saveBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = Object.assign(document.createElement('a'), { href: url, download: name });
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
